import React, { useState, useMemo } from 'react';
import {
  Settings2,
  Search,
  Filter,
  RefreshCw,
  Info,
  CheckCircle2,
  XCircle,
  Zap,
  Database,
  Save,
  Undo2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { FeatureCategorySection } from '@/components/features/feature-category-section';
import type { FeatureMode } from '@/types/features';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type FilterType = 'all' | 'live' | 'demo' | 'coming-soon' | 'api-required';

const categoryConfig = [
  {
    id: 'core-operations',
    name: 'Core Operations',
    description: 'Essential features for daily asset management operations',
  },
  {
    id: 'transaction-management',
    name: 'Transaction Management',
    description: 'Financial transactions and asset value tracking',
  },
  {
    id: 'operational-tools',
    name: 'Operational Tools',
    description: 'Tools for asset tracking and compliance management',
  },
  {
    id: 'analytics-reporting',
    name: 'Analytics & Reporting',
    description: 'Data insights and historical tracking capabilities',
  },
  {
    id: 'system-administration',
    name: 'System Administration',
    description: 'Bulk operations and data management tools',
  },
  {
    id: 'configuration',
    name: 'Configuration',
    description: 'System settings and reference data management',
  },
];

export default function FeaturesSettingsPage() {
  const {
    modules,
    isLoading,
    isSaving,
    isDirty,
    recentlyChanged,
    switchMode,
    switchAllToMode,
    getModulesByCategory,
    resetToDefaults,
    saveSettings,
    discardChanges,
  } = useFeatureSettings();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');

  // Statistics
  const stats = useMemo(() => {
    const allModules = Object.values(modules);
    return {
      total: allModules.length,
      live: allModules.filter(m => m.mode === 'live').length,
      demo: allModules.filter(m => m.mode === 'demo').length,
      comingSoon: allModules.filter(m => m.status === 'coming-soon').length,
      apiRequired: allModules.filter(m => m.apiRequired).length,
    };
  }, [modules]);

  // Filter modules
  const filteredModules = useMemo(() => {
    let filtered = Object.values(modules);

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        m =>
          m.name.toLowerCase().includes(query) ||
          m.description.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(m => {
        switch (filterType) {
          case 'live':
            return m.mode === 'live';
          case 'demo':
            return m.mode === 'demo';
          case 'coming-soon':
            return m.status === 'coming-soon';
          case 'api-required':
            return m.apiRequired;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [modules, searchQuery, filterType]);

  const handleModeChange = async (moduleId: string, newMode: FeatureMode) => {
    const module = modules[moduleId];
    if (!module) return;

    const success = await switchMode(moduleId, newMode);

    if (success) {
      toast.success('Mode Changed Successfully', {
        description: `${module.name} is now in ${newMode} mode.`,
      });
    } else {
      toast.error('Failed to Change Mode', {
        description: 'An error occurred while switching modes. Please try again.',
      });
    }
  };

  const handleBulkAction = async (mode: FeatureMode) => {
    const success = await switchAllToMode(mode);

    if (success) {
      toast.success('Bulk Action Completed', {
        description: `All available modules switched to ${mode} mode.`,
      });
    } else {
      toast.error('Bulk Action Failed', {
        description: 'An error occurred during bulk operation. Please try again.',
      });
    }
  };

  const handleResetToDefaults = () => {
    resetToDefaults();
    toast.success('Settings Reset', {
      description: 'All feature settings have been reset to defaults.',
    });
  };

  const handleSaveSettings = async () => {
    const success = await saveSettings();
    if (success) {
      toast.success('Settings Saved', {
        description: 'Your feature settings have been saved successfully.',
      });
    } else {
      toast.error('Failed to Save Settings', {
        description: 'An error occurred while saving settings. Please try again.',
      });
    }
  };

  const handleDiscardChanges = () => {
    discardChanges();
    toast.info('Changes Discarded', {
      description: 'Your unsaved changes have been discarded.',
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5">
                <Settings2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Feature Settings</h1>
                <p className="text-sm text-muted-foreground">
                  Manage module modes and configure feature availability
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetToDefaults}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Reset to Defaults
              </Button>
            </div>
          </div>

          {/* Info Banner */}
          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Demo Mode</strong> uses sample data for testing and training.{' '}
              <strong>Live Mode</strong> connects to real data and performs actual operations.
            </AlertDescription>
          </Alert>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Modules</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Settings2 className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Live Mode</p>
                  <p className="text-2xl font-bold text-green-600">{stats.live}</p>
                </div>
                <Database className="h-8 w-8 text-green-600/50" />
              </div>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Demo Mode</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.demo}</p>
                </div>
                <Zap className="h-8 w-8 text-gray-600/50" />
              </div>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Coming Soon</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.comingSoon}</p>
                </div>
                <Info className="h-8 w-8 text-amber-600/50" />
              </div>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">API Required</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.apiRequired}</p>
                </div>
                <XCircle className="h-8 w-8 text-blue-600/50" />
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search features..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={filterType} onValueChange={(value: string) => setFilterType(value as FilterType)}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Features</SelectItem>
                <SelectItem value="live">Live Mode</SelectItem>
                <SelectItem value="demo">Demo Mode</SelectItem>
                <SelectItem value="coming-soon">Coming Soon</SelectItem>
                <SelectItem value="api-required">API Required</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('live')}
                disabled={isLoading}
                className="gap-2"
              >
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                All to Live
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('demo')}
                disabled={isLoading}
                className="gap-2"
              >
                <XCircle className="h-4 w-4 text-gray-600" />
                All to Demo
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-8">
          {filteredModules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No features found</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Try adjusting your search query or filters to find what you're looking for.
              </p>
            </div>
          ) : (
            categoryConfig.map((category) => {
              const categoryModules = getModulesByCategory(category.id).filter(module =>
                filteredModules.some(fm => fm.id === module.id)
              );

              return (
                <FeatureCategorySection
                  key={category.id}
                  title={category.name}
                  description={category.description}
                  modules={categoryModules}
                  onModeChange={handleModeChange}
                  isLoading={isLoading}
                  recentlyChanged={recentlyChanged}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Footer with Save/Discard buttons */}
      <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {isDirty ? (
              <span className="text-amber-600 font-medium">You have unsaved changes</span>
            ) : (
              <span>All changes saved</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscardChanges}
              disabled={!isDirty || isSaving}
              className="gap-2"
            >
              <Undo2 className="h-4 w-4" />
              Discard Changes
            </Button>
            <Button
              size="sm"
              onClick={handleSaveSettings}
              disabled={!isDirty || isSaving}
              className="gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
